/**
 * ADR-0222 + ADR-0224 — "Business skills" group.
 *
 * Rendered inside `SettingsSkills.tsx` above the "Add custom skill"
 * button for **every** site type (ADR-0224 removes the non-store gate;
 * stores see the group too with an "optional for stores" line).
 *
 * Seven fixed rows — one per reserved business-skill id — each with:
 *   - a one-line description,
 *   - an inline URL editor (`InlineEditValue`),
 *   - an on/off Toggle gated by URL presence,
 *   - a server-side non-blocking warning line (off-domain URL for
 *     pricing / demo / sign-up / contact / trust) when applicable.
 *
 * The `get_pricing` row additionally renders a compact Plans editor
 * (ADR-0224): merchant-typed plans with name / price / period /
 * description / optional sign-up URL. Up to 10 plans per site. The
 * dashboard sends the full list on every PUT; the server replaces the
 * stored list atomically.
 */

import { useEffect, useMemo, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Pill, Toggle } from '../../components/atoms/index.js';
import { InlineEditValue } from '../readiness/InlineEditValue.js';
import { isBookingHostUrl, isStatusHostUrl, isTrustCenterHostUrl } from '@sill/types';
import {
  BUSINESS_SKILL_SLOTS,
  listBusinessSkills,
  putBusinessSkill,
  type BusinessPlanPeriod,
  type BusinessSkillId,
  type BusinessSkillItem,
  type BusinessSkillPlan,
} from '../../api/site-business-skills.js';
import { SAVE_REASON_COPY, SLOT_COPY } from './business-skill-copy.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

// Per-slot + save-error copy live in `business-skill-copy.ts` (shared
// with the onboarding Skills step suggestions, ADR-0226).

const PERIOD_OPTIONS: readonly { value: BusinessPlanPeriod; label: string }[] = [
  { value: 'month', label: 'per month' },
  { value: 'year', label: 'per year' },
  { value: 'one_time', label: 'one-time' },
  { value: 'custom', label: 'custom' },
];


/** Hosts that are expected to be off the merchant's domain for a slot. */
const OFF_DOMAIN_OK: Partial<Record<string, (url: string) => boolean>> = {
  book_meeting: isBookingHostUrl,
  get_status: isStatusHostUrl,
  get_trust_center: isTrustCenterHostUrl,
};

export interface BusinessSkillsSectionProps {
  readonly site_id: string;
  readonly site_domain: string;
  /** When `true`, the group header carries an "optional for stores"
   *  note. The group is always rendered (ADR-0224); the header text
   *  only differs for stores. */
  readonly platform_kind?: 'store' | 'website' | null;
}

export function BusinessSkillsSection({
  site_id,
  site_domain,
  platform_kind = null,
}: BusinessSkillsSectionProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const toast = useToast();
  const auth = useOptionalAuth();
  const role =
    auth !== null && auth.state.kind === 'authenticated'
      ? auth.state.user.role
      : null;
  const can_write = role === 'owner' || role === 'admin';

  const [items, setItems] = useState<BusinessSkillItem[] | null>(null);

  async function refresh(): Promise<void> {
    if (http === null) return;
    const res = await listBusinessSkills(http, site_id);
    if (res.kind === 'ok') {
      const by_id = new Map(res.items.map((i) => [i.skill_id, i]));
      setItems(
        BUSINESS_SKILL_SLOTS.map((slot) => {
          const hit = by_id.get(slot);
          if (hit !== undefined) return hit;
          return {
            skill_id: slot,
            url: null,
            label: null,
            enabled: false,
            suggested_url: null,
            warning: null,
            plans: null,
          };
        }),
      );
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site_id]);

  async function saveUrl(slot: BusinessSkillId, url: string): Promise<void> {
    if (http === null) throw new Error('Not connected.');
    const res = await putBusinessSkill(http, site_id, slot, { url });
    if (res.kind === 'ok') {
      void refresh();
      return;
    }
    if (res.kind === 'bad_request') {
      throw new Error(SAVE_REASON_COPY[res.reason] ?? 'Could not save.');
    }
    if (res.kind === 'forbidden') {
      throw new Error('You do not have permission to change this.');
    }
    if (res.kind === 'not_found') {
      throw new Error('Site not found.');
    }
    throw new Error(res.message);
  }

  async function useDetected(slot: BusinessSkillId, url: string): Promise<void> {
    if (http === null) return;
    const res = await putBusinessSkill(http, site_id, slot, { url, enabled: true });
    if (res.kind === 'ok') {
      toast({
        title: 'Saved',
        body: 'Your agent card updates within a few minutes.',
        tone: 'mint',
      });
      void refresh();
      return;
    }
    const body =
      res.kind === 'bad_request'
        ? SAVE_REASON_COPY[res.reason] ?? 'Could not save.'
        : res.kind === 'forbidden'
          ? 'You do not have permission to change this.'
          : res.kind === 'not_found'
            ? 'Site not found.'
            : res.message;
    toast({ title: 'Save failed', body, tone: 'coral' });
  }

  async function toggleEnabled(slot: BusinessSkillId, next: boolean): Promise<void> {
    if (http === null) return;
    const current = items?.find((i) => i.skill_id === slot) ?? null;
    setItems((prev) =>
      prev === null
        ? prev
        : prev.map((i) => (i.skill_id === slot ? { ...i, enabled: next } : i)),
    );
    const res = await putBusinessSkill(http, site_id, slot, { enabled: next });
    if (res.kind === 'ok') return;
    setItems((prev) =>
      prev === null
        ? prev
        : prev.map((i) =>
            i.skill_id === slot
              ? { ...i, enabled: current?.enabled ?? false }
              : i,
          ),
    );
    const body =
      res.kind === 'bad_request'
        ? SAVE_REASON_COPY[res.reason] ?? 'Could not update.'
        : res.kind === 'forbidden'
          ? 'You do not have permission to change this.'
          : res.kind === 'not_found'
            ? 'Site not found.'
            : res.message;
    toast({ title: 'Update failed', body, tone: 'coral' });
  }

  async function savePlans(
    next_plans: readonly BusinessSkillPlan[] | null,
  ): Promise<void> {
    if (http === null) return;
    const res = await putBusinessSkill(http, site_id, 'get_pricing', {
      plans: next_plans,
    });
    if (res.kind === 'ok') {
      toast({
        title: 'Plans saved',
        body: 'Your agent card updates within a few minutes.',
        tone: 'mint',
      });
      void refresh();
      return;
    }
    const body =
      res.kind === 'bad_request'
        ? SAVE_REASON_COPY[res.reason] ?? 'Could not save plans.'
        : res.kind === 'forbidden'
          ? 'You do not have permission to change this.'
          : res.kind === 'not_found'
            ? 'Site not found.'
            : res.message;
    toast({ title: 'Save failed', body, tone: 'coral' });
  }

  const rows = items ?? [];
  const is_store = platform_kind === 'store';

  return (
    <div
      id="skills-list-business"
      data-testid="business-skills-section"
      className="mb-5 pt-5"
      style={{ borderTop: `1px solid ${C.border}` }}
    >
      <div className="mb-4 pb-3" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 18,
            color: C.text,
            letterSpacing: -0.4,
            marginBottom: 4,
          }}
        >
          Business skills
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          Seven link-returning skills — point agents to the right page on your site.
          {is_store ? ' Optional for stores; off by default.' : ''}
        </div>
      </div>

      <div className="py-2 space-y-2">
        {rows.map((row) => {
          const copy = SLOT_COPY[row.skill_id];
          const is_pricing = row.skill_id === 'get_pricing';
          return (
            <div
              key={row.skill_id}
              data-testid={`business-skill-row-${row.skill_id}`}
              className="p-3"
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                opacity: row.enabled ? 1 : 0.9,
              }}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
                      {copy.name}
                    </span>
                    <span data-testid={`business-skill-pill-${row.skill_id}`}>
                      <Pill tone={row.enabled && row.url !== null ? 'mint' : 'neutral'}>
                        {row.enabled && row.url !== null ? 'LIVE' : 'OFF'}
                      </Pill>
                    </span>
                  </div>
                  <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
                    {row.skill_id}
                  </div>
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.muted,
                      marginTop: 4,
                      lineHeight: 1.5,
                    }}
                  >
                    {copy.description}
                  </div>
                  <div className="mt-3">
                    <InlineEditValue
                      label=""
                      value={row.url}
                      kind="url"
                      site_domain={site_domain}
                      {...(OFF_DOMAIN_OK[row.skill_id] !== undefined ? { allowOffDomain: OFF_DOMAIN_OK[row.skill_id] } : {})}
                      placeholder={copy.placeholder}
                      prefill={row.suggested_url}
                      editLabel={row.url === null ? 'Add URL' : 'Edit URL'}
                      onSave={(next) => saveUrl(row.skill_id, next)}
                      data_testid={`business-skill-url-${row.skill_id}`}
                    />
                  </div>
                  {row.url === null && row.suggested_url !== null && (
                    <div
                      data-testid={`business-skill-suggested-${row.skill_id}`}
                      className="mt-3"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                        padding: '10px 12px',
                        background: C.surface3 ?? C.surface2,
                        border: `1px solid ${C.border}`,
                        borderRadius: 3,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: monoFont,
                          fontSize: 10,
                          color: C.muted,
                          letterSpacing: '0.14em',
                          textTransform: 'uppercase',
                        }}
                      >
                        Found on your homepage
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <a
                          href={row.suggested_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: C.text,
                            fontFamily: monoFont,
                            fontSize: 13,
                            textDecoration: 'underline',
                            textDecorationColor: C.dim,
                            textUnderlineOffset: '3px',
                            wordBreak: 'break-all',
                            flex: '1 1 auto',
                            minWidth: 0,
                          }}
                        >
                          {row.suggested_url}
                        </a>
                        {can_write && (
                          <button
                            type="button"
                            data-testid={`business-skill-use-this-${row.skill_id}`}
                            onClick={() => void useDetected(row.skill_id, row.suggested_url!)}
                            style={{
                              fontFamily: sansFont,
                              fontSize: 13,
                              padding: '6px 12px',
                              minHeight: 32,
                              background: C.lime,
                              color: C.ink,
                              border: `1px solid ${C.lime}`,
                              borderRadius: 3,
                              cursor: 'pointer',
                            }}
                          >
                            Use this
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {row.warning !== null && (
                    <div
                      data-testid={`business-skill-warning-${row.skill_id}`}
                      style={{
                        fontFamily: sansFont,
                        fontSize: 12,
                        color: C.amber,
                        marginTop: 6,
                      }}
                    >
                      {row.warning}
                    </div>
                  )}
                  {is_pricing && (
                    <PlansEditor
                      can_write={can_write}
                      plans={row.plans ?? []}
                      onSave={savePlans}
                    />
                  )}
                </div>
                <Toggle
                  on={row.enabled}
                  disabled={!can_write || row.url === null}
                  onChange={(next) => void toggleEnabled(row.skill_id, next)}
                  aria-label={`Toggle ${copy.name}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ================================================================== */
/* Plans editor (get_pricing only)                                     */
/* ================================================================== */

interface PlansEditorProps {
  readonly can_write: boolean;
  readonly plans: readonly BusinessSkillPlan[];
  readonly onSave: (next: readonly BusinessSkillPlan[] | null) => Promise<void>;
}

interface DraftPlan {
  name: string;
  period: BusinessPlanPeriod;
  price_kind: 'amount' | 'custom';
  amount_decimal: string;
  currency: string;
  description: string;
  sign_up_url: string;
}

function planToDraft(p: BusinessSkillPlan): DraftPlan {
  return {
    name: p.name,
    period: p.period,
    price_kind: p.price.kind,
    amount_decimal: p.price.kind === 'amount' ? p.price.amount_decimal : '',
    currency: p.price.kind === 'amount' ? p.price.currency : 'USD',
    description: p.description ?? '',
    sign_up_url: p.sign_up_url ?? '',
  };
}

function draftToPlan(d: DraftPlan): BusinessSkillPlan {
  const base: {
    name: string;
    price: BusinessSkillPlan['price'];
    period: BusinessPlanPeriod;
    description?: string;
    sign_up_url?: string;
  } = {
    name: d.name.trim(),
    period: d.period,
    price:
      d.price_kind === 'custom'
        ? { kind: 'custom' }
        : {
            kind: 'amount',
            amount_decimal: d.amount_decimal.trim(),
            currency: d.currency.trim().toUpperCase(),
          },
  };
  if (d.description.trim().length > 0) base.description = d.description.trim();
  if (d.sign_up_url.trim().length > 0) base.sign_up_url = d.sign_up_url.trim();
  return base;
}

function PlansEditor({
  can_write,
  plans,
  onSave,
}: PlansEditorProps): JSX.Element {
  const C = useC();
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<DraftPlan[]>(() =>
    plans.length > 0
      ? plans.map(planToDraft)
      : [],
  );
  const [saving, setSaving] = useState(false);

  // Reset drafts whenever the saved list changes (post-save refresh).
  useEffect(() => {
    if (!editing) {
      setDrafts(plans.map(planToDraft));
    }
  }, [plans, editing]);

  const summary = useMemo(() => {
    if (plans.length === 0) return 'No plans listed yet.';
    return plans
      .map((p) =>
        p.price.kind === 'custom'
          ? `${p.name} (contact us)`
          : `${p.name} — ${p.price.currency} ${p.price.amount_decimal}/${p.period}`,
      )
      .join(', ');
  }, [plans]);

  function addRow(): void {
    if (drafts.length >= 10) return;
    setDrafts((prev) => [
      ...prev,
      {
        name: '',
        period: 'month',
        price_kind: 'amount',
        amount_decimal: '',
        currency: 'USD',
        description: '',
        sign_up_url: '',
      },
    ]);
  }

  function removeRow(idx: number): void {
    setDrafts((prev) => prev.filter((_, i) => i !== idx));
  }

  function patch(idx: number, patch_fn: (d: DraftPlan) => DraftPlan): void {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? patch_fn(d) : d)));
  }

  async function commit(): Promise<void> {
    setSaving(true);
    try {
      const next = drafts.map(draftToPlan);
      await onSave(next.length === 0 ? null : next);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  function cancel(): void {
    setDrafts(plans.map(planToDraft));
    setEditing(false);
  }

  return (
    <div
      data-testid="plans-editor"
      className="mt-4 pt-3"
      style={{ borderTop: `1px dashed ${C.border}` }}
    >
      <div
        className="flex items-center justify-between gap-3 flex-wrap"
        style={{ marginBottom: 6 }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}
        >
          Plans
        </div>
        {!editing && can_write && (
          <button
            type="button"
            data-testid="plans-edit"
            onClick={() => setEditing(true)}
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              padding: '4px 10px',
              minHeight: 28,
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              cursor: 'pointer',
            }}
          >
            {plans.length === 0 ? 'Add plans' : 'Edit plans'}
          </button>
        )}
      </div>
      {!editing && (
        <div
          data-testid="plans-summary"
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: plans.length === 0 ? C.muted : C.text,
            lineHeight: 1.6,
          }}
        >
          {summary}
        </div>
      )}
      {editing && (
        <div className="space-y-3">
          {drafts.map((d, idx) => (
            <div
              key={idx}
              data-testid={`plans-row-${idx}`}
              className="p-3"
              style={{
                background: C.surface3 ?? C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={d.name}
                  onChange={(e) => patch(idx, (p) => ({ ...p, name: e.target.value }))}
                  placeholder="Plan name (e.g. Pro)"
                  maxLength={80}
                  data-testid={`plans-row-${idx}-name`}
                  style={{
                    flex: '2 1 180px',
                    minWidth: 140,
                    fontFamily: sansFont,
                    fontSize: 13,
                    padding: '6px 8px',
                    background: C.bg,
                    color: C.text,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                  }}
                />
                <select
                  value={d.price_kind}
                  onChange={(e) =>
                    patch(idx, (p) => ({
                      ...p,
                      price_kind: e.target.value as 'amount' | 'custom',
                    }))
                  }
                  data-testid={`plans-row-${idx}-price-kind`}
                  style={{
                    fontFamily: sansFont,
                    fontSize: 13,
                    padding: '6px 8px',
                    background: C.bg,
                    color: C.text,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                  }}
                >
                  <option value="amount">Amount</option>
                  <option value="custom">Contact us</option>
                </select>
                {d.price_kind === 'amount' && (
                  <>
                    <input
                      type="text"
                      value={d.amount_decimal}
                      onChange={(e) =>
                        patch(idx, (p) => ({ ...p, amount_decimal: e.target.value }))
                      }
                      placeholder="19.00"
                      inputMode="decimal"
                      maxLength={13}
                      data-testid={`plans-row-${idx}-amount`}
                      style={{
                        width: 100,
                        fontFamily: monoFont,
                        fontSize: 13,
                        padding: '6px 8px',
                        background: C.bg,
                        color: C.text,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                      }}
                    />
                    <input
                      type="text"
                      value={d.currency}
                      onChange={(e) =>
                        patch(idx, (p) => ({ ...p, currency: e.target.value.toUpperCase() }))
                      }
                      placeholder="USD"
                      maxLength={3}
                      data-testid={`plans-row-${idx}-currency`}
                      style={{
                        width: 60,
                        fontFamily: monoFont,
                        fontSize: 13,
                        padding: '6px 8px',
                        background: C.bg,
                        color: C.text,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                      }}
                    />
                  </>
                )}
                <select
                  value={d.period}
                  onChange={(e) =>
                    patch(idx, (p) => ({ ...p, period: e.target.value as BusinessPlanPeriod }))
                  }
                  data-testid={`plans-row-${idx}-period`}
                  style={{
                    fontFamily: sansFont,
                    fontSize: 13,
                    padding: '6px 8px',
                    background: C.bg,
                    color: C.text,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                  }}
                >
                  {PERIOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <input
                type="text"
                value={d.description}
                onChange={(e) => patch(idx, (p) => ({ ...p, description: e.target.value }))}
                placeholder="Short description (optional, up to 140 chars)"
                maxLength={140}
                data-testid={`plans-row-${idx}-description`}
                style={{
                  fontFamily: sansFont,
                  fontSize: 13,
                  padding: '6px 8px',
                  background: C.bg,
                  color: C.text,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                }}
              />
              <input
                type="text"
                value={d.sign_up_url}
                onChange={(e) => patch(idx, (p) => ({ ...p, sign_up_url: e.target.value }))}
                placeholder="Sign-up link for this plan (optional)"
                maxLength={2048}
                data-testid={`plans-row-${idx}-sign-up-url`}
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  padding: '6px 8px',
                  background: C.bg,
                  color: C.text,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  data-testid={`plans-row-${idx}-remove`}
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    padding: '4px 10px',
                    minHeight: 28,
                    background: 'transparent',
                    color: C.coral,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    cursor: 'pointer',
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {drafts.length < 10 && (
              <button
                type="button"
                onClick={addRow}
                data-testid="plans-add-row"
                style={{
                  fontFamily: sansFont,
                  fontSize: 13,
                  padding: '6px 12px',
                  minHeight: 32,
                  background: 'transparent',
                  color: C.text,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  cursor: 'pointer',
                }}
              >
                Add plan
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button
              type="button"
              onClick={cancel}
              disabled={saving}
              data-testid="plans-cancel"
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                padding: '6px 12px',
                minHeight: 32,
                background: 'transparent',
                color: C.muted,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void commit()}
              disabled={saving}
              data-testid="plans-save"
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                padding: '6px 14px',
                minHeight: 32,
                background: C.lime,
                color: C.ink,
                border: `1px solid ${C.lime}`,
                borderRadius: 2,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: 500,
              }}
            >
              {saving ? 'Saving…' : 'Save plans'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
